import { CheckSquare, Download, FolderInput, Loader2, MinusSquare, Square, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BulkDownloadState } from "@/hooks/useBulkDownload";
import { cn } from "@/lib/utils";

interface Props {
  count: number;
  /** Everything on screen that could be selected, for the select-all toggle. */
  selectableCount: number;
  /** More items exist than are loaded; select-all then covers them too. */
  moreAvailable?: boolean;
  /** Select-all is still loading the remaining pages; `count` is what has loaded so far. */
  selectingAll?: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  /** Select-all toggled off; defaults to `onClear`. */
  onDeselectAll?: () => void;
  /** ZIP download; omitted where the items have no files (Elements). */
  onDownload?: () => void;
  download?: BulkDownloadState;
  /** Open the move-to-project picker for the selection. */
  onMove?: () => void;
  /** Gallery only: move every selected generation to Trash (confirmed by the page). */
  onTrash?: () => void;
  busy?: boolean;
  /** Accessible name of the toolbar. */
  label?: string;
  className?: string;
}

const IDLE_DOWNLOAD: BulkDownloadState = { running: false, done: 0, total: 0, failed: 0 };

export function SelectionToolbar({ count, selectableCount, moreAvailable = false, selectingAll = false, onSelectAll, onClear, onDeselectAll = onClear, onDownload, download = IDLE_DOWNLOAD, onMove, onTrash, busy = false, label = "Selected generations", className }: Props) {
  const allSelected = count > 0 && count >= selectableCount && !moreAvailable;
  const ToggleIcon = selectingAll ? Loader2 : allSelected ? CheckSquare : count > 0 ? MinusSquare : Square;
  const selectAllLabel = moreAvailable ? "Select all" : `Select all ${selectableCount}`;
  const locked = busy || download.running || selectingAll;
  const partLabel = download.parts && download.parts > 1 ? ` ${download.part}/${download.parts}` : "";
  const failure = !download.running && download.failed > 0
    ? download.error || "ZIP could not be downloaded. Please try again."
    : "";
  return (
    <div
      role="toolbar"
      aria-label={label}
      data-testid="selection-toolbar"
      className={cn(
        "flex max-w-full flex-wrap items-center gap-1.5 rounded-2xl border border-studio-border-light bg-studio-panel/95 p-1.5 shadow-panel backdrop-blur",
        className
      )}
    >
      <button
        type="button"
        onClick={allSelected ? onDeselectAll : onSelectAll}
        disabled={locked}
        aria-pressed={allSelected}
        title={allSelected ? "Clear selection" : selectAllLabel}
        aria-label={selectingAll ? `Loading every item to select, ${count} so far` : `${count} selected. ${allSelected ? "Clear selection" : selectAllLabel}`}
        data-testid="select-all"
        className="inline-flex h-9 items-center gap-2 rounded-lg px-2.5 text-xs font-semibold text-studio-ink hover:bg-studio-card focus-visible:outline-none focus-visible:shadow-glow-sm"
      >
        <ToggleIcon className={cn("h-4 w-4 text-studio-accent", selectingAll && "animate-spin")} />
        {selectingAll ? (
          <span role="status" className="tabular-nums">Loading all…</span>
        ) : (
          <span className="tabular-nums">
            {count}<span className="hidden sm:inline"> selected</span>
            {!allSelected && <span className="ml-1.5 font-medium text-studio-accent">· <span className="sm:hidden">All</span><span className="hidden sm:inline">Select all</span></span>}
          </span>
        )}
      </button>
      {onDownload && (
        <Button type="button" size="sm" onClick={onDownload} loading={download.running} disabled={locked || count === 0} aria-label="Download ZIP" className="sm:h-9">
          {download.running ? (
            <span role="status">Preparing ZIP{partLabel}…</span>
          ) : (
            <>
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Download </span>ZIP
            </>
          )}
        </Button>
      )}
      {onMove && (
        <Button type="button" size="sm" variant={onDownload ? "secondary" : "primary"} onClick={onMove} disabled={locked || count === 0} aria-label="Move to project" className="sm:h-9">
          <FolderInput className="h-4 w-4" />
          <span className="hidden sm:inline">Move to project</span>
        </Button>
      )}
      {onTrash && (
        <Button type="button" size="sm" variant="secondary" onClick={onTrash} disabled={locked || count === 0} aria-label="Move to Trash" className="sm:h-9">
          <Trash2 className="h-4 w-4" />
          <span className="hidden sm:inline">Trash</span>
        </Button>
      )}
      <button
        type="button"
        onClick={onClear}
        disabled={busy || download.running}
        aria-label="Clear selection"
        title="Clear selection"
        className="flex h-9 w-9 items-center justify-center rounded-lg text-studio-muted hover:bg-studio-card hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm"
      >
        <X className="h-4 w-4" />
      </button>
      <span role="alert" className={cn("px-2 text-xs text-studio-danger", !failure && "sr-only")}>{failure}</span>
    </div>
  );
}
